import { CaseServiceInfo, InsuranceCase } from '../models/insurance-case.model';
import { Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { CommonService } from '../../shared/services/common.service';
import { SCConfig } from '../../sc-config/constant/sc-config.constant';
import { pairwise, startWith } from 'rxjs/operators';
import { ComponentService } from '../../shared/services/component.service';
import { TestingCategoryModel } from '../../shared/models/TestingCategoryModel';
import { ComponentSeed } from '../../shared/models/domain/ComponentSeed';
import { ServiceProduct } from '../../shared/models/domain/ServiceProduct';
import { DocumentScanConfiguration } from '../../shared/models/domain/DocumentScanConfiguration';
import { Vat } from '../../shared/models/domain/Vat';
import { TranslateService } from '@ngx-translate/core';
import { SeedMetaData } from '../../shared/models/domain/SeedMetaData';
import { DocumentInfo } from '../../shared/models/domain/DocumentInfo';
import { ServiceProductPrice } from '../../shared/models/domain/ServiceProductPrice';
import { InsuranceCompanySeed } from '../../shared/models/domain/InsuranceCompanySeed';

import {
    ConnectedWithInsuranceCase,
    InsuranceConnectedCase
} from '../../shared/models/responseModels/InsuranceConnectedCase';
import { PCXConstants } from '../../sc-config/constant/PCXConstants';
import { ClaimOrderModel } from '../../shared/models/responseModels/ClaimOrderModel';
import { UtilityService } from '../../shared/standalone-packages/ecap3/core';
@Injectable({
    providedIn: 'root'
})
export class CaseCommonService {
    caseComponentSeedData = new BehaviorSubject<ComponentSeed[]>(null);
    InsuranceCompanySeedData = new BehaviorSubject<any>(null);
    caseUpdateFormTouched = new BehaviorSubject<any>(null);
    formGroup = new BehaviorSubject<any>(null);
    caseUnitSeedData = new BehaviorSubject<any>(null);
    stickyViewForSubTotalCost = new BehaviorSubject<FormGroup>(null);
    InspectorAnswerList: any;
    lang$: Observable<string>;
    timeValueInPercentage: number;
    disConnectedMailList = new BehaviorSubject<any>([]);
    subitemDeleteSectionOpen = new BehaviorSubject<any>(null);
    confirmedSubitemDeletion = new BehaviorSubject<any>(null);
    numberOfSeletedSubitemsForDelete = new BehaviorSubject<number>(0);
    private filteredComponentSeedsForCaseDetails = new BehaviorSubject<ComponentSeed[]>([]);
    filteredComponentSeedsForCaseDetails$ = this.filteredComponentSeedsForCaseDetails.asObservable();
    private testingCategorySubject = new BehaviorSubject<TestingCategoryModel[]>([]);
    public testingCategorySeedDataList$: Observable<TestingCategoryModel[]> =
        this.testingCategorySubject.asObservable();
    private serviceProductsSubject = new BehaviorSubject<ServiceProduct[]>([]);
    public serviceProducts$ = this.serviceProductsSubject.asObservable();
    private documentScanConfigurationSubject = new BehaviorSubject<DocumentScanConfiguration>(null);
    public documentScanConfiguration$: Observable<DocumentScanConfiguration> =
        this.documentScanConfigurationSubject.asObservable();
    private vatsSubject = new BehaviorSubject<Vat[]>([]);
    public vats$ = this.vatsSubject.asObservable();
    public liabilityAssessmentChangeSubject = new BehaviorSubject<boolean>(false);
    public deletedItem$ = new BehaviorSubject<CaseServiceInfo>(null);
    public updatedCaseDocumentInfo$ = new BehaviorSubject<DocumentInfo[]>(null);
    public subitemViewModalOpen = new BehaviorSubject<boolean>(false);
    public caseServiceSubTotalPriceChanged$ = new BehaviorSubject<boolean>(false);
    public caseServiceDeleted$ = new BehaviorSubject<boolean>(false);
    inspectorSelectionChanged$ = new BehaviorSubject<boolean>(false);
    isServiceActiveChanged$ = new BehaviorSubject<boolean>(false);
    private formValueChangeSubscriptions: Subscription[] = [];
    public currentCaseInsuranceCompany?: InsuranceCompanySeed = null;
    constructor(
        private readonly commonService: CommonService,
        private readonly componentService: ComponentService,
        private readonly translateService: TranslateService,
        private readonly utilityService: UtilityService,
    ) {
        this.translateService.onLangChange.subscribe(lang => {
            const languageKey = CommonService.getLanguageKeyFromLang(this.translateService.currentLang);
            const testingCategories = this.caseComponentSeedData.getValue();
            this.caseComponentSeedData.next(
                (testingCategories || []).sort((a, b) => {
                    return SeedMetaData.Compare(a, b, languageKey);
                })
            );
        });
    }

    getInspectorQuestionList(questionList) {
        const inspectorQuestionData: any[] = [];
        if (questionList && questionList.length > 0) {
            questionList.forEach(ques => {
                if (ques.QuestionOptionList === null && ques.QuestionSectionList !== null) {
                    ques.QuestionSectionList.forEach(section => {
                        inspectorQuestionData.push({
                            QuestionId: ques.ItemId,
                            QuestionTitle: ques.QuestionTitle,
                            SectionId: section.SectionId,
                            SectionTitle: section.SectionTitle,
                            QuestionOptionList: section.QuestionOptionList,
                            HasRemarks: section.HasRemarks
                        });
                    });
                } else {
                    inspectorQuestionData.push({
                        QuestionId: ques.ItemId,
                        QuestionTitle: ques.QuestionTitle,
                        SectionId: ques.SectionId || null,
                        SectionTitle: ques.SectionTitle || null,
                        QuestionOptionList: ques.QuestionOptionList,
                        HasRemarks: ques.HasRemarks
                    });
                }
            });
        }
        return inspectorQuestionData;
    }

    calculateTotal(subItem: FormGroup | AbstractControl, isPriceMismatched?) {
        const unitPrice = parseFloat(subItem.get('UnitPrice').value) || 0;
        const quantity = parseFloat(subItem.get('Quantity').value) || 0;
        const total = unitPrice * quantity;
        if (subItem.get('RoundsTo').value === SCConfig.RoundsTo[1]) {
            const roundFigure = this.commonService.floatingValueToRoundFigure(total, true);
            subItem.get('TotalPrice').setValue(parseFloat(roundFigure.toFixed(2)));
        } else {
            const roundFigure = this.commonService.floatingValueToRoundFigure(total, false);
            subItem.get('TotalPrice').setValue(parseFloat(roundFigure.toFixed(2)));
        }

        if (isPriceMismatched) {
            subItem.get('IsTotalPriceMismatched').setValue(false);
        }
    }
    calculateAndSetSubTotalPrice(
        subItemList: FormGroup[],
        serviceForm: FormGroup,
        isEdit?,
        useFulCompoenentLifeCycle?
    ) {
        let total: number = 0;
        subItemList.forEach(subItem => {
            if (subItem && !subItem.get('IsSubItemDeleted').value) {
                total = total + parseFloat(subItem.get('TotalPrice').value || 0);
            }
        });
        const roundFigure = this.commonService.floatingValueToRoundFigure(total, false);
        serviceForm.get('InitialSubTotalPrice').setValue(parseFloat(roundFigure.toFixed(2)));
        serviceForm.get('SubTotalPrice').setValue(parseFloat(roundFigure.toFixed(2)));
        if (!isEdit) {
            serviceForm.get('InitialNewSubTotalPrice').setValue(parseFloat(roundFigure.toFixed(2)));
            serviceForm.get('NewSubTotalPrice').setValue(parseFloat(roundFigure.toFixed(2)));
        }
        if (!isEdit) {
            const serviceAdditionalData = serviceForm.get('ServiceAdditionalData') as FormGroup;
            this.calculateDamageRelatedAndRepairDamageCost(serviceForm, subItemList, serviceAdditionalData, isEdit);
            let totalRepairDamageCost = serviceAdditionalData.get('TotalRepairDamageCost').value;
            this.calculateLiabilityEvaluation(serviceForm, totalRepairDamageCost, useFulCompoenentLifeCycle);
            this.calculateNewFairValue(serviceAdditionalData, serviceForm);
        }
    }

    calculateDamageRelatedAndRepairDamageCost(
        serviceForm: FormGroup,
        subItemList: FormGroup[],
        serviceAdditionalInfo: FormGroup,
        isEdit?
    ) {
        let totalDamageRelatedCost: number = 0;
        let totalRepairDamageCost: number = 0;
        if (serviceAdditionalInfo.get('LiabilityAssessment').value) {
            subItemList.forEach(subItem => {
                const inspectorInputForm = subItem.get('InspectorSubItemInput') as FormGroup;
                if (subItem.get('IsDamageRelatedCost').value) {
                    if (isEdit) {
                        totalDamageRelatedCost =
                            totalDamageRelatedCost + parseFloat(inspectorInputForm.get('TotalPrice').value || 0);
                    } else {
                        totalDamageRelatedCost =
                            totalDamageRelatedCost + parseFloat(subItem.get('TotalPrice').value || 0);
                    }
                } else {
                    if (isEdit) {
                        totalRepairDamageCost =
                            totalRepairDamageCost + parseFloat(inspectorInputForm.get('TotalPrice').value || 0);
                    } else {
                        totalRepairDamageCost =
                            totalRepairDamageCost + parseFloat(subItem.get('TotalPrice').value || 0);
                    }
                }
            });
        }
        const totalDamageRelatedCostRoundFigure = this.commonService.floatingValueToRoundFigure(totalDamageRelatedCost);
        const totalRepairDamageCostRoundFigure = this.commonService.floatingValueToRoundFigure(totalRepairDamageCost);

        serviceAdditionalInfo
            .get('TotalDamageRelatedCost')
            .setValue(parseFloat(totalDamageRelatedCostRoundFigure.toFixed(2)));
        serviceAdditionalInfo
            .get('TotalRepairDamageCost')
            .setValue(parseFloat(totalRepairDamageCostRoundFigure.toFixed(2)));
        this.calculateLiabilityEvaluation(serviceForm, totalRepairDamageCost);
        this.calculateNewFairValue(serviceAdditionalInfo, serviceForm);
    }

    calculateFairValueAndTimeValue(serviceAdditionalData: FormGroup, serviceForm: FormGroup) {
        let remainingUsefulLifePercentagePoint = 0;
        const liability = this.getLiabilityPercentageValue();

        if (serviceAdditionalData.get('IsTimeValueSufficient').value) {
            serviceAdditionalData.get('ServiceItemFairValue').setValue(0);
            serviceAdditionalData.get('ServiceItemTimeValue').setValue(0);
        } else {
            let newValue = parseFloat(serviceAdditionalData.get('NewFairValue').value || 0);
            let usefulLifePlainValue = serviceForm.get('UseFullLife').value;
            let usefulLife = parseFloat(serviceForm.get('UseFullLife').value) || 0;
            let age = parseFloat(serviceAdditionalData.get('UpdatedAge').value || 0);
            if (usefulLifePlainValue !== null) {
                if (usefulLife === 0) {
                    this.addRemarksForLiabilityAssessment();
                    remainingUsefulLifePercentagePoint = liability;
                } else {
                    remainingUsefulLifePercentagePoint = (usefulLife - age) / usefulLife;
                    if (remainingUsefulLifePercentagePoint < liability) {
                        this.addRemarksForLiabilityAssessment();
                        remainingUsefulLifePercentagePoint = liability;
                    } else {
                        this.addRemarksForLiabilityAssessment();
                    }
                }

                this.timeValueInPercentage = remainingUsefulLifePercentagePoint;
                const timeValuePercentageRoundFigure = this.commonService.floatingValueToRoundFigure(
                    remainingUsefulLifePercentagePoint * 100
                );
                serviceAdditionalData
                    .get('ServiceItemTimeValue')
                    .setValue(parseFloat(timeValuePercentageRoundFigure.toFixed(2)));
                let fairValue = newValue * remainingUsefulLifePercentagePoint;
                const fairValueRoundFigure = this.commonService.floatingValueToRoundFigure(fairValue);
                serviceAdditionalData.get('ServiceItemFairValue').setValue(parseFloat(fairValueRoundFigure.toFixed(2)));
            }
        }
    }
    /**
     * the liability evaluation percentage, returns 0 if liability toggle is enabled in insurance company, returns the threshold otherwise
     * @returns if 20% then returns 0.2
     */
    getLiabilityPercentageValue(): number {
        return this.currentCaseInsuranceCompany?.IsLiabilityEvaluationMinimumThresholdPercentageEnabled === false
            ? (this.currentCaseInsuranceCompany?.LiabilityEvaluationMinimumThresholdPercentage ?? 0) / 100
            : 0;
    }

    calculateAndSetNewSubTotalPrice(
        subItemList: FormGroup[],
        serviceForm: FormGroup,
        isEdit?,
        useFulCompoenentLifeCycle?
    ) {
        let total: number = 0;
        subItemList.forEach(subItem => {
            if (subItem && !subItem.get('IsSubItemDeleted').value) {
                const inspectorInputForm = subItem.get('InspectorSubItemInput') as FormGroup;
                total = total + parseFloat(inspectorInputForm.get('TotalPrice').value || 0);
            }
        });
        const totalRoundFigure = this.commonService.floatingValueToRoundFigure(total);
        serviceForm.get('InitialNewSubTotalPrice').setValue(parseFloat(totalRoundFigure.toFixed(2)));
        serviceForm.get('NewSubTotalPrice').setValue(parseFloat(totalRoundFigure.toFixed(2)));
        const serviceAdditionalData = serviceForm.get('ServiceAdditionalData') as FormGroup;
        this.calculateDamageRelatedAndRepairDamageCost(serviceForm, subItemList, serviceAdditionalData, isEdit);
        let totalRepairDamageCost = serviceAdditionalData.get('TotalRepairDamageCost').value;
        this.calculateLiabilityEvaluation(serviceForm, totalRepairDamageCost, useFulCompoenentLifeCycle);
        this.calculateNewFairValue(serviceAdditionalData, serviceForm);
    }

    resetLiabilityRelatedFields(serviceAdditionalData: FormGroup) {
        serviceAdditionalData.get('LiabilityAssesmentType').setValue(null);
        serviceAdditionalData.get('NewFairValue').setValue(null);
        serviceAdditionalData.get('LiabilityAssesmentComment').setValue(null);
        serviceAdditionalData.get('IsTimeValueSufficient').setValue(false);
        serviceAdditionalData.get('ServiceItemFairValue').setValue(0);
        serviceAdditionalData.get('ServiceItemTimeValue').setValue(0);
        serviceAdditionalData.get('TotalDamageRelatedCost').setValue(0);
        serviceAdditionalData.get('TotalRepairDamageCost').setValue(0);
    }

    subscriptionForFormValueChange(subItemList: FormGroup[], serviceForm: FormGroup, isEdit) {
        // Unsubscribe from all existing subscriptions
        this.unsubscribeFormValueChanges();

        const serviceAdditionalData = serviceForm.get('ServiceAdditionalData') as FormGroup;
        this.formValueChangeSubscriptions.push(
            serviceAdditionalData.get('LiabilityAssessment').valueChanges.subscribe(res => {
                this.liabilityAssessmentChangeSubject.next(serviceAdditionalData.get('LiabilityAssessment').value);
                if (serviceAdditionalData.get('LiabilityAssessment').value) {
                    this.calculateFairValueAndTimeValue(serviceAdditionalData, serviceForm);
                    this.calculateDamageRelatedAndRepairDamageCost(
                        serviceForm,
                        subItemList,
                        serviceAdditionalData,
                        isEdit
                    );
                    this.calculateNewFairValue(serviceAdditionalData, serviceForm);
                } else {
                    this.resetLiabilityRelatedFields(serviceAdditionalData);
                    subItemList.forEach(subItem => {
                        subItem.get('IsDamageRelatedCost').setValue(false);
                    });
                }
            })
        );
        subItemList.forEach(subItem => {
            const inspectorInputForm = subItem.get('InspectorSubItemInput') as FormGroup;
            let inspectorUnitPriceWasInitiallyBlank = false;
            this.formValueChangeSubscriptions.push(
                subItem.get('UnitPrice').valueChanges.subscribe(value => {
                    if (!inspectorInputForm.get('UnitPrice').value) {
                        inspectorUnitPriceWasInitiallyBlank = true;
                    }
                    // this.compareTotalPrice(subItem , inspectorInputForm);
                    this.calculateTotal(
                        subItem,
                        subItem.get('IsTotalPriceMismatched') && subItem.get('IsTotalPriceMismatched').value
                    );
                    this.calculateAndSetSubTotalPrice(subItemList, serviceForm, isEdit);
                    if (subItem.get('IsNewSubItem').value && inspectorUnitPriceWasInitiallyBlank) {
                        inspectorInputForm.get('UnitPrice').setValue(parseFloat(value));
                    }
                    if (!isEdit) {
                        inspectorInputForm.get('UnitPrice').setValue(parseFloat(value));
                    }
                    this.compareTotalPrice(serviceForm);
                })
            );
            let inspectorQuantityWasInitiallyBlank = false;
            this.formValueChangeSubscriptions.push(
                subItem.get('Quantity').valueChanges.subscribe(value => {
                    if (!inspectorInputForm.get('Quantity').value) {
                        inspectorQuantityWasInitiallyBlank = true;
                    }
                    this.calculateTotal(
                        subItem,
                        subItem.get('IsTotalPriceMismatched') && subItem.get('IsTotalPriceMismatched').value
                    );
                    this.calculateAndSetSubTotalPrice(subItemList, serviceForm, serviceAdditionalData, isEdit);
                    if (subItem.get('IsNewSubItem').value && inspectorQuantityWasInitiallyBlank) {
                        inspectorInputForm.get('Quantity').setValue(parseFloat(value));
                    }
                    if (!isEdit) {
                        inspectorInputForm.get('Quantity').setValue(parseFloat(value));
                    }
                    // this.compareTotalPrice(subItem , inspectorInputForm);
                    this.compareTotalPrice(serviceForm);
                })
            );
            this.formValueChangeSubscriptions.push(
                subItem
                    .get('Unit')
                    .valueChanges.pipe(startWith(null as string), pairwise())
                    .subscribe(([prev, next]: [any, any]) => {
                        let previousValueOfSubItemUnit = prev;
                        if (
                            previousValueOfSubItemUnit === null ||
                            previousValueOfSubItemUnit === inspectorInputForm.get('Unit').value
                        ) {
                            inspectorInputForm.get('Unit').setValue(next);
                        }
                    })
            );

            this.formValueChangeSubscriptions.push(
                subItem.get('IsDamageRelatedCost').valueChanges.subscribe(value => {
                    this.calculateDamageRelatedAndRepairDamageCost(
                        serviceForm,
                        subItemList,
                        serviceAdditionalData,
                        isEdit
                    );
                })
            );
            this.formValueChangeSubscriptions.push(
                subItem.get('TotalPrice').valueChanges.subscribe(value => {
                    this.calculateDamageRelatedAndRepairDamageCost(
                        serviceForm,
                        subItemList,
                        serviceAdditionalData,
                        isEdit
                    );
                    //  this.compareTotalPrice(subItem , inspectorInputForm);
                    this.compareTotalPrice(serviceForm);
                })
            );
            this.formValueChangeSubscriptions.push(
                inspectorInputForm.get('TotalPrice').valueChanges.subscribe(value => {
                    //  this.compareTotalPrice(subItem , inspectorInputForm);
                    this.calculateDamageRelatedAndRepairDamageCost(
                        serviceForm,
                        subItemList,
                        serviceAdditionalData,
                        isEdit
                    );
                    this.compareTotalPrice(serviceForm);
                })
            );
            this.formValueChangeSubscriptions.push(
                inspectorInputForm.get('UnitPrice').valueChanges.subscribe(value => {
                    this.calculateTotal(inspectorInputForm);
                    // this.compareTotalPrice(subItem , inspectorInputForm);
                    this.calculateAndSetNewSubTotalPrice(subItemList, serviceForm, isEdit);
                    this.compareTotalPrice(serviceForm);
                })
            );
            this.formValueChangeSubscriptions.push(
                inspectorInputForm.get('Quantity').valueChanges.subscribe(value => {
                    this.calculateTotal(inspectorInputForm);
                    this.calculateAndSetNewSubTotalPrice(subItemList, serviceForm, isEdit);
                    this.compareTotalPrice(serviceForm);
                })
            );
            this.formValueChangeSubscriptions.push(
                subItem.get('IsSubItemDeleted').valueChanges.subscribe(value => {
                    this.calculateAndSetSubTotalPrice(subItemList, serviceForm, isEdit);
                    this.calculateAndSetNewSubTotalPrice(subItemList, serviceForm, isEdit);
                })
            );
        });
        this.formValueChangeSubscriptions.push(
            serviceAdditionalData.get('UpdatedAge').valueChanges.subscribe(value => {
                if (isEdit) {
                    this.calculateAndSetNewSubTotalPrice(subItemList, serviceForm, isEdit);
                } else {
                    this.calculateAndSetSubTotalPrice(subItemList, serviceForm, isEdit);
                }
                this.calculateFairValueAndTimeValue(serviceAdditionalData, serviceForm);
            })
        );

        this.formValueChangeSubscriptions.push(
            serviceAdditionalData.get('IsTimeValueSufficient').valueChanges.subscribe(value => {
                this.calculateFairValueAndTimeValue(serviceAdditionalData, serviceForm);
                if (!value) {
                    this.calculateLiabilityEvaluation(
                        serviceForm,
                        serviceAdditionalData.get('TotalRepairDamageCost').value
                    );
                }
            })
        );

        this.formValueChangeSubscriptions.push(
            serviceAdditionalData.get('NewFairValue').valueChanges.subscribe(value => {
                this.calculateFairValueAndTimeValue(serviceAdditionalData, serviceForm);
                this.calculateLiabilityEvaluation(
                    serviceForm,
                    serviceAdditionalData.get('TotalRepairDamageCost').value
                );
            })
        );
        this.formValueChangeSubscriptions.push(
            serviceAdditionalData.get('LiabilityAssessment').valueChanges.subscribe(value => {
                if (isEdit) {
                    this.calculateAndSetNewSubTotalPrice(subItemList, serviceForm, isEdit);
                } else {
                    this.calculateAndSetSubTotalPrice(subItemList, serviceForm, isEdit);
                }
            })
        );

        this.formValueChangeSubscriptions.push(
            serviceAdditionalData.get('CaseComponentId').valueChanges.subscribe(value => {
                if (value === null) {
                    serviceForm.get('UseFullLife').setValue(null);
                } else {
                    const nestedSubscription = this.caseComponentSeedData.subscribe(caseComponentList => {
                        const componentUseFullLife =
                            parseFloat(
                                this.commonService.inputIdMapping(
                                    caseComponentList,
                                    'ItemId',
                                    value,
                                    'ComponentUsefulLife'
                                ) || ''
                            ) || 0;
                        serviceForm.get('UseFullLife').setValue(componentUseFullLife);
                    });
                    this.formValueChangeSubscriptions.push(nestedSubscription);
                }

                if (isEdit) {
                    this.calculateAndSetNewSubTotalPrice(subItemList, serviceForm, isEdit);
                } else {
                    this.calculateAndSetSubTotalPrice(subItemList, serviceForm, isEdit);
                }
            })
        );
        this.formValueChangeSubscriptions.push(
            serviceForm.get('UseFullLife').valueChanges.subscribe(value => {
                if (value === null) {
                    serviceAdditionalData.get('LiabilityValuation').setValue(null);
                    serviceAdditionalData.get('ServiceItemTimeValue').setValue(null);
                    serviceAdditionalData.get('ServiceItemFairValue').setValue(null);
                    serviceAdditionalData.get('NewFairValue').setValue(null);
                    this.addRemarksForLiabilityAssessment();
                } else {
                    if (isEdit) {
                        this.calculateAndSetNewSubTotalPrice(subItemList, serviceForm, isEdit);
                    } else {
                        this.calculateAndSetSubTotalPrice(subItemList, serviceForm, isEdit);
                    }
                    this.calculateFairValueAndTimeValue(serviceAdditionalData, serviceForm);
                    this.addRemarksForLiabilityAssessment();
                }
            })
        );

        this.formValueChangeSubscriptions.push(
            serviceForm.get('IsUseFullLifeEditable').valueChanges.subscribe(value => {
                // if the value is false, we'll have to generate the ComponentLife from the existing id
                if (!value) {
                    let caseComponentId = serviceAdditionalData.get('CaseComponentId').value || '';
                    const nestedSubscription = this.caseComponentSeedData.subscribe(caseComponentList => {
                        const componentUseFullLife =
                            parseFloat(
                                this.commonService.inputIdMapping(
                                    caseComponentList,
                                    'ItemId',
                                    caseComponentId,
                                    'ComponentUsefulLife'
                                ) || ''
                            ) || null;

                        serviceForm.get('UseFullLife').setValue(componentUseFullLife);
                        this.calculateLiabilityEvaluation(
                            serviceForm,
                            serviceAdditionalData.get('TotalRepairDamageCost').value
                        );
                    });
                    this.formValueChangeSubscriptions.push(nestedSubscription);
                }
            })
        );

        this.formValueChangeSubscriptions.push(
            serviceForm
                .get('SubTotalPrice')
                .valueChanges.pipe(startWith(null as string), pairwise())
                .subscribe(([prev, next]: [any, any]) => {
                    this.compareTotalPrice(serviceForm);
                    if (prev != next) {
                        //this.calculateSubtotalAndNewSubtotalWithRounding(serviceForm);
                        this.calculateSubTotalWithRounding(serviceForm, false);
                    }
                })
        );
        this.formValueChangeSubscriptions.push(
            serviceForm
                .get('NewSubTotalPrice')
                .valueChanges.pipe(startWith(null as string), pairwise())
                .subscribe(([prev, next]: [any, any]) => {
                    this.compareTotalPrice(serviceForm);
                    if (prev != next) {
                        this.calculateNewSubTotalWithRounding(serviceForm, false);
                        // this.calculateLiabilityEvaluation(serviceForm, next);
                    }
                })
        );
    }

    private unsubscribeFormValueChanges(): void {
        this.formValueChangeSubscriptions.forEach(subscription => {
            if (subscription && !subscription.closed) {
                subscription.unsubscribe();
            }
        });
        this.formValueChangeSubscriptions = [];
    }

    calculateNewSubTotalWithRounding(serviceItem: FormGroup, isInitialValue: boolean) {
        const newSubtotalPrice = isInitialValue
            ? serviceItem.get('InitialNewSubTotalPrice').value
            : serviceItem.get('NewSubTotalPrice').value;
        if (serviceItem.get('RoundsTo').value === SCConfig.RoundsTo[1]) {
            const newSubTotalRoundFigure = this.commonService.floatingValueToRoundFigure(newSubtotalPrice, true);
            serviceItem.get('NewSubTotalPrice').setValue(parseFloat(newSubTotalRoundFigure.toFixed(2)));
        } else {
            const newSubTotalRoundFigure = this.commonService.floatingValueToRoundFigure(newSubtotalPrice, false);
            serviceItem.get('NewSubTotalPrice').setValue(parseFloat(newSubTotalRoundFigure.toFixed(2)));
        }
    }

    calculateSubTotalWithRounding(serviceItem: FormGroup, isInitialValue: boolean) {
        const subtotalPrice = isInitialValue
            ? serviceItem.get('InitialSubTotalPrice').value
            : serviceItem.get('SubTotalPrice').value;
        if (serviceItem.get('RoundsTo').value === SCConfig.RoundsTo[1]) {
            const subTotalRoundFigure = this.commonService.floatingValueToRoundFigure(subtotalPrice, true);
            serviceItem.get('SubTotalPrice').setValue(parseFloat(subTotalRoundFigure.toFixed(2)));
        } else {
            const subTotalRoundFigure = this.commonService.floatingValueToRoundFigure(subtotalPrice, false);
            serviceItem.get('SubTotalPrice').setValue(parseFloat(subTotalRoundFigure.toFixed(2)));
        }
    }

    calculateSubtotalAndNewSubtotalWithRounding(serviceItem: FormGroup) {
        const subTotalPrice = serviceItem.get('InitSubTotalPrice').value;
        const newSubtotalPrice = serviceItem.get('InitNewSubTotalPrice').value;
        if (serviceItem.get('RoundsTo').value === SCConfig.RoundsTo[1]) {
            const subTotalRoundFigure = this.commonService.floatingValueToRoundFigure(subTotalPrice, true);
            const newSubTotalRoundFigure = this.commonService.floatingValueToRoundFigure(newSubtotalPrice, true);
            serviceItem.get('SubTotalPrice').setValue(parseFloat(subTotalRoundFigure.toFixed(2)));
            serviceItem.get('NewSubTotalPrice').setValue(parseFloat(newSubTotalRoundFigure.toFixed(2)));
        } else {
            const subTotalRoundFigure = this.commonService.floatingValueToRoundFigure(subTotalPrice, false);
            const newSubTotalRoundFigure = this.commonService.floatingValueToRoundFigure(newSubtotalPrice, false);
            serviceItem.get('SubTotalPrice').setValue(parseFloat(subTotalRoundFigure.toFixed(2)));
            serviceItem.get('NewSubTotalPrice').setValue(parseFloat(newSubTotalRoundFigure.toFixed(2)));
        }
    }

    calculateLiabilityEvaluation(serviceForm, total, componentUseLife?) {
        const serviceAdditionalData = serviceForm.get('ServiceAdditionalData') as FormGroup;
        if (serviceAdditionalData.get('IsNewValueEditable').value) {
            total = serviceAdditionalData.get('NewFairValue').value;
        }
        if (serviceAdditionalData.get('LiabilityAssessment').value) {
            const age = parseFloat(serviceAdditionalData.get('UpdatedAge').value) || 0;
            const componentUseLife = parseFloat(serviceForm.get('UseFullLife').value);
            const componentUseLifePlain = serviceForm.get('UseFullLife').value;
            if (componentUseLifePlain === null) {
                serviceAdditionalData.get('LiabilityValuation').setValue(null);
            } else {
                this.calculateFairValueAndTimeValue(serviceAdditionalData, serviceForm);
                let liabilityEvaluation =
                    (total * (componentUseLife - age)) / (componentUseLife > 0 ? componentUseLife : 1);
                if (!serviceAdditionalData.get('IsTimeValueSufficient').value) {
                    liabilityEvaluation = total * this.timeValueInPercentage;
                }
                const liabilityEvaluationRoundFigure =
                    this.commonService.floatingValueToRoundFigure(liabilityEvaluation);

                serviceAdditionalData
                    .get('LiabilityValuation')
                    .setValue(parseFloat(liabilityEvaluationRoundFigure.toFixed(2)));
            }
        }
    }
    calculateNewFairValue(serviceAdditionalData, serviceForm) {
        if (!serviceAdditionalData.get('IsNewValueEditable').value) {
            if (serviceForm.get('UseFullLife').value !== null) {
                serviceAdditionalData
                    .get('NewFairValue')
                    .setValue(serviceAdditionalData.get('TotalRepairDamageCost').value);
            }
        }
    }

    haveLiabilityAssessmentInAnyService(caseServiceList: FormGroup[]) {
        for (const services of caseServiceList ?? []) {
            let serviceAdditionalData: FormGroup = services.get('ServiceAdditionalData') as FormGroup;
            if (serviceAdditionalData.get('LiabilityAssessment').value && services.get('UseFullLife').value !== null) {
                return true;
            }
        }

        return false;
    }

    checkTimeValueInPercentageLessThanThreshold(caseServiceList: FormGroup[]) {
        let remainingUsefulLife: number;
        const threshold = this.getLiabilityPercentageValue();
        for (const services of caseServiceList ?? []) {
            let serviceAdditionalData: FormGroup = services.get('ServiceAdditionalData') as FormGroup;
            let userFullLife: number =
                services.get('UseFullLife').value >= 0 ? services.get('UseFullLife').value : null;
            let age: number = serviceAdditionalData.get('UpdatedAge').value || 0;
            if (userFullLife !== null) {
                if (userFullLife !== 0) {
                    remainingUsefulLife = (userFullLife - age) / userFullLife;
                } else {
                    remainingUsefulLife = 0;
                }

                if (remainingUsefulLife < threshold) {
                    return true;
                }
            }
        }
        return false;
    }

    addRemarksForLiabilityAssessment() {
        this.formGroup.subscribe(formGroup => {
            this.InspectorAnswerList = formGroup.get('InspectorAnswerList').controls as FormGroup[];
            const caseServiceList = formGroup.get('CaseServiceList').controls as FormGroup[];
            if (this.haveLiabilityAssessmentInAnyService(caseServiceList)) {
                const index = new FormArray(this.InspectorAnswerList).value.findIndex(
                    item => item.QuestionId === SCConfig.LiabilityQuestionId
                );
                if (
                    (index !== -1 && this.InspectorAnswerList[index].get('SelectedOptionId').value === null) ||
                    this.InspectorAnswerList[index].get('SelectedOptionId').value.length === 0
                ) {
                    this.InspectorAnswerList[index]
                        .get('SelectedOptionId')
                        .setValue('7a4d32af-1d10-4c9e-b8fa-6d6368325482');
                }
            }
            if (
                this.haveLiabilityAssessmentInAnyService(caseServiceList) &&
                this.checkTimeValueInPercentageLessThanThreshold(caseServiceList)
            ) {
                formGroup.get('LiabilityAssesmentAdditinalComment').setValue('MINIMUM_TIME_VALUE_INFO_MESSAGE');
            } else {
                formGroup.get('LiabilityAssesmentAdditinalComment').setValue(null);
            }
        });
    }
    compareTotalPrice(serviceForm: FormGroup) {
        let subTotalPrice = parseFloat(serviceForm.get('SubTotalPrice').value);
        let newSubTotalPrice = parseFloat(serviceForm.get('NewSubTotalPrice').value);
        if (newSubTotalPrice > subTotalPrice) {
            serviceForm.get('IsNegativeServiceSavedCost').setValue(true);
        } else {
            serviceForm.get('IsNegativeServiceSavedCost').setValue(false);
        }
        // let inspectorTotalPrice : number = parseFloat(inspectorInputForm.get("TotalPrice").value);
        // let adminTotalPrice : number = parseFloat(subItem.get("TotalPrice").value);
        // if(inspectorTotalPrice > adminTotalPrice) { // isAcceptTotalPrice
        //     inspectorInputForm.get("isAcceptTotalPrice").setValue(false);
        // }else {
        //     inspectorInputForm.get("isAcceptTotalPrice").setValue(true);
        // }
    }

    // bring only the needed components by analyzing the CaseServiceList
    public getFilteredComponentSeedForCaseDetailsPage(caseServiceList: CaseServiceInfo[] = null) {
        let componentIds = Array<string>();
        if (caseServiceList != null && caseServiceList.length > 0) {
            caseServiceList.forEach(caseService => {
                if (
                    caseService!.ServiceAdditionalData != null &&
                    caseService!.ServiceAdditionalData!.CaseComponentId != null &&
                    caseService!.ServiceAdditionalData!.CaseComponentId != ''
                ) {
                    componentIds.push(caseService.ServiceAdditionalData.CaseComponentId);
                }
            });
        }
        this.componentService.getComponentSeed(componentIds).subscribe(componentSeeds => {
            this.filteredComponentSeedsForCaseDetails.next(componentSeeds);
        });
    }

    public setTestingCategorySubject(data: TestingCategoryModel[]) {
        this.testingCategorySubject.next(data);
    }

    public setServiceProductsSubject(data: ServiceProduct[]) {
        this.serviceProductsSubject.next(data);
    }

    public setDocumentScanConfiguration(data: DocumentScanConfiguration) {
        this.documentScanConfigurationSubject.next(data);
    }

    public setVatsSubject(data: Vat[]) {
        data = data.sort((a, b) => {
            return a.Value > b.Value ? 1 : -1;
        });
        this.vatsSubject.next(data);
    }

    public mergeConnectedAndFraudCase(
        connectedCases: InsuranceCase[],
        fraudCases: InsuranceCase[],
        action: string = SCConfig.FRAUD_CASE_ACTION.ADD
    ) {
        let concatedConnectedCases = null;
        if (action === SCConfig.FRAUD_CASE_ACTION.ADD) {
            concatedConnectedCases = connectedCases.concat(fraudCases);
        } else if (action === SCConfig.FRAUD_CASE_ACTION.REMOVE) {
            concatedConnectedCases = connectedCases;
        }

        const duplicateCaseList = this.getUniqueInsuranceCase(concatedConnectedCases);
        const selectedConnectedCaseIds = duplicateCaseList.map(item => item.ItemId);
        return {
            duplicateCases: this.sortInsuranceCaseByCreateDate(duplicateCaseList),
            selectedConnectedCase: selectedConnectedCaseIds
        };
    }

    public getUniqueInsuranceCase(insuranceCases: InsuranceCase[]) {
        let uniqueMapper = new Map<string, InsuranceCase>();
        insuranceCases?.forEach(insurance => {
            if (!uniqueMapper.has(insurance.ItemId)) {
                uniqueMapper.set(insurance.ItemId, insurance);
            }
        });
        return Array.from(uniqueMapper.values());
    }

    public sortInsuranceCaseByCreateDate(insuranceCases: InsuranceCase[]) {
        return insuranceCases.sort((a, b) => {
            const dateA = a.CreateDate ? new Date(a.CreateDate) : new Date(0);
            const dateB = b.CreateDate ? new Date(b.CreateDate) : new Date(0);
            return dateB.getTime() - dateA.getTime();
        });
    }

    public updateDocumentProcessingStatus(caseDocuments: any[]) {
        (caseDocuments || []).forEach(item => {
            if (item.SelectedForParashift && item.TemporaryProcessingStatus === 0) {
                item.ProcessingStatus = 0;
                delete item.TemporaryProcessingStatus;
            }
        });
    }

    public selectIpexRequestedServiceForCase(
        caseServiceList: CaseServiceInfo[],
        activeServiceProductIds: string[],
        serviceProductPrices: ServiceProductPrice[],
        orderDate: Date
    ): string {
        // Helper: check if a service is active
        const isActiveServiceProduct = (serviceProductId: string) => activeServiceProductIds.includes(serviceProductId);

        // Count all items
        let itemCount = 0;
        let sum = 0;
        (caseServiceList || []).forEach(service => {
            if (service?.IsServiceActive) {
                itemCount += 1;
                sum += service.SubTotalPrice;
            }
        });

        // Rule 1: more than 4 items and ipex check complex is active
        if (
            itemCount > 4 &&
            isActiveServiceProduct(SCConfig.ServiceProducts.CheckComplexId) &&
            this.hasValidProductPrice(SCConfig.ServiceProducts.CheckComplexId, serviceProductPrices, orderDate)
        ) {
            return SCConfig.ServiceProducts.CheckComplexId;
        }
        // Rule 2: sum >= 20000 and ipex check complex is active
        if (
            sum >= 20000 &&
            isActiveServiceProduct(SCConfig.ServiceProducts.CheckComplexId) &&
            this.hasValidProductPrice(SCConfig.ServiceProducts.CheckComplexId, serviceProductPrices, orderDate)
        ) {
            return SCConfig.ServiceProducts.CheckComplexId;
        }
        // Rule 3: sum >= 5000 and ipex check plus is active
        if (
            sum >= 5000 &&
            isActiveServiceProduct(SCConfig.ServiceProducts.CheckPlusId) &&
            this.hasValidProductPrice(SCConfig.ServiceProducts.CheckPlusId, serviceProductPrices, orderDate)
        ) {
            return SCConfig.ServiceProducts.CheckPlusId;
        }
        // Default: ipex check
        return SCConfig.ServiceProducts.CheckId;
    }

    public hasValidProductPrice(
        serviceProductId: string,
        serviceProductPrices: ServiceProductPrice[],
        orderDate: Date
    ) {
        const hasValidProductPrice = serviceProductPrices.some(productPrice => {
            const validFromDate = new Date(productPrice.ValidFrom);
            return productPrice.ServiceProductId === serviceProductId && orderDate >= validFromDate;
        });
        return hasValidProductPrice;
    }

    inspectorSelectionChanged() {
        this.inspectorSelectionChanged$.next(true);
    }

    correctCaseDataPhoneNumbers(caseData: InsuranceCase) {
        if (!caseData) return;
        caseData.InsuranceOfficerPhoneNumber = this.commonService.getParsedPhoneNumberFromString(
            caseData.InsuranceOfficerPhoneNumber
        );

        if (caseData.PolicyHolderInfo) {
            caseData.PolicyHolderInfo.PhoneNumber = this.commonService.getParsedPhoneNumberFromString(
                caseData.PolicyHolderInfo.PhoneNumber
            );
        }

        if (caseData.CaseServiceList && caseData.CaseServiceList.length > 0) {
            caseData.CaseServiceList.forEach(service => {
                this.correctCaseServiceInfoPhoneNumbers(service);
            });
        }
    }

    correctCaseServiceInfoPhoneNumbers(service: CaseServiceInfo) {
        service.ConstructionCompanyPhone = this.commonService.getParsedPhoneNumberFromString(
            service.ConstructionCompanyPhone
        );
        if (service.ServiceAdditionalData) {
            service.ServiceAdditionalData.ConstructionCompanyPhone = this.commonService.getParsedPhoneNumberFromString(
                service.ServiceAdditionalData.ConstructionCompanyPhone
            );
        }
    }

    public prepareConnectedRepairCases(duplicateRepairCaseList: ClaimOrderModel[],permanentCreateCaseForm:FormGroup): InsuranceConnectedCase | null {
            if (!duplicateRepairCaseList?.length) {
                return null;
            }
    
            const connectedCase = new InsuranceConnectedCase();
            connectedCase.ItemId = this.utilityService.getNewGuid();
            connectedCase.InsuranceClaimNumber = permanentCreateCaseForm.get('InsuranceClaimNumber').value;
            connectedCase.ConnectedCaseInfos = [];
            connectedCase.ConnectedCaseInfos = duplicateRepairCaseList.map(duplicateRepairCase => {
                const connectedCaseInfo = new ConnectedWithInsuranceCase();
                connectedCaseInfo.CaseId = duplicateRepairCase.orderId;
                connectedCaseInfo.InsuranceClaimNumber = duplicateRepairCase.claimNumberInsurance;
                connectedCaseInfo.OrderDate = duplicateRepairCase.orderDate;
                connectedCaseInfo.ConnectedCaseType = PCXConstants.REPAIR_DIGITAL_CASE;
                return connectedCaseInfo;
            });
    
            return connectedCase;
        }
}
